<template>
    <div class="create-post-container">
        <div class="header">
            <el-page-header @back="$router.back()" content="发布新帖" />
        </div>

        <el-card class="form-card">
            <el-form :model="form" :rules="rules" ref="formRef" label-position="top">
                <el-form-item label="标题" prop="title" v-if="form.type !== 'moment'">
                    <el-input v-model="form.title" placeholder="请输入标题，简洁明了" />
                </el-form-item>

                <el-form-item label="类型" prop="type">
                    <el-radio-group v-model="form.type">
                        <el-radio-button label="moment">新鲜事</el-radio-button>
                        <el-radio-button label="question">提问</el-radio-button>
                        <el-radio-button label="article">学习分享</el-radio-button>
                    </el-radio-group>
                </el-form-item>

                <el-form-item label="内容" prop="content">
                    <template v-if="form.type !== 'moment'">
                        <v-md-editor v-model="form.content" height="500px" placeholder="支持 Markdown 和 LaTeX 公式..."
                            :disabled-menus="[]" @upload-image="handleUploadImage" />
                    </template>
                    <template v-else>
                        <div class="moment-editor-container">
                            <v-md-editor v-model="form.content" height="300px"
                                placeholder="分享你的新鲜事吧！支持 #话题 和 Markdown 语法" :disabled-menus="[]"
                                @upload-image="handleUploadImage" />

                            <div class="moment-upload-section">
                                <el-upload :action="uploadUrl" :headers="uploadHeaders" list-type="picture-card"
                                    :on-preview="handlePictureCardPreview" :on-remove="handleRemove"
                                    :on-success="handleUploadSuccess" :before-upload="beforeUpload"
                                    :file-list="fileList" accept="image/jpeg,image/png,image/gif">
                                    <el-icon>
                                        <Plus />
                                    </el-icon>
                                </el-upload>

                                <el-dialog v-model="dialogVisible" align-center>
                                    <img w-full :src="dialogImageUrl" alt="Preview Image" style="width: 100%" />
                                </el-dialog>
                            </div>
                        </div>
                    </template>
                </el-form-item>

                <div class="form-actions">
                    <el-button @click="$router.back()">取消</el-button>
                    <el-button type="primary" :loading="submitting" @click="submitForm">
                        确认发布
                    </el-button>
                </div>
            </el-form>
        </el-card>
    </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import { Plus } from '@element-plus/icons-vue';
import api from '@/utils/api';

const router = useRouter();
const formRef = ref(null);
const submitting = ref(false);

const uploadUrl = ref('/api/upload/image');
const uploadHeaders = computed(() => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
});

const fileList = ref([]);
const dialogImageUrl = ref('');
const dialogVisible = ref(false);

const form = reactive({
    title: '',
    type: 'moment', // Default to moment
    content: '',
});

const rules = computed(() => ({
    title: form.type === 'moment' ? [] : [
        { required: true, message: '请输入标题', trigger: 'blur' },
        { min: 5, max: 100, message: '标题长度在 5 到 100 个字符之间', trigger: 'blur' },
    ],
    type: [
        { required: true, message: '请选择类型', trigger: 'change' },
    ],
    content: [
        { required: true, message: '请输入内容', trigger: 'blur' },
        { min: 10, message: '内容至少 10 个字符', trigger: 'blur' },
    ],
}));

const handleUploadImage = async (event, insertImage, files) => {
    for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        try {
            const response = await api.post('/api/upload/image', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            insertImage({
                url: response.data.url,
                desc: file.name,
            });
        } catch (error) {
            ElMessage.error('图片上传失败');
        }
    }
};

const handleRemove = (uploadFile, uploadFiles) => {
    fileList.value = uploadFiles;
};

const handlePictureCardPreview = (uploadFile) => {
    dialogImageUrl.value = uploadFile.url;
    dialogVisible.value = true;
};

const handleUploadSuccess = (response, uploadFile, uploadFiles) => {
    if (response.success && response.url) {
        uploadFile.url = response.url;
        fileList.value = uploadFiles;
    } else {
        ElMessage.error('图片上传失败');
        // Remove failed file
        fileList.value = uploadFiles.filter(f => f.uid !== uploadFile.uid);
    }
};

const beforeUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    const isLt10M = file.size / 1024 / 1024 < 10;

    if (!isImage) {
        ElMessage.error('上传头像图片只能是图片格式!');
    }
    if (!isLt10M) {
        ElMessage.error('上传头像图片大小不能超过 10MB!');
    }

    // Add upload type to distinguish logic
    return isImage && isLt10M;
};

const submitForm = async () => {
    if (!formRef.value) return;

    await formRef.value.validate(async (valid) => {
        if (valid) {
            submitting.value = true;
            try {
                // Construct final content for moment posts
                let submitFormValues = { ...form };
                if (submitFormValues.type === 'moment') {
                    let imageMarkdown = '';
                    if (fileList.value && fileList.value.length > 0) {
                        imageMarkdown = '\n\n' + fileList.value
                            .filter(f => f.url) // Ensure it was successful
                            .map(f => `![image](${f.url})`)
                            .join('\n');
                    }
                    submitFormValues.content = submitFormValues.content + imageMarkdown;
                }

                const response = await api.post('/api/forum/posts', submitFormValues);
                ElMessage.success('发布成功');
                router.push(`/forum/${response.data.id}`);
            } catch (error) {
                if (error.response?.status === 403) {
                    ElMessage.warning(error.response.data.error || '内容包含敏感词，请修改后重试');
                } else {
                    ElMessage.error(error.response?.data?.error || '发布失败');
                }
            } finally {
                submitting.value = false;
            }
        }
    });
};
</script>

<style scoped>
.create-post-container {
    max-width: 1000px;
    margin: 0 auto;
    padding: 20px;
}

.header {
    margin-bottom: 20px;
}

.form-card {
    border-radius: 8px;
}

.form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 15px;
    margin-top: 20px;
}

:deep(.v-md-editor) {
    border: 1px solid #dcdfe6;
    border-radius: 4px;
}

.moment-editor-container {
    width: 100%;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    background-color: #fcfcfc;
}

:deep(.moment-editor-container .el-textarea__inner) {
    border: none;
    box-shadow: none;
    padding: 15px;
    font-size: 15px;
    resize: none;
    background-color: transparent;
}

:deep(.moment-editor-container .el-textarea__inner:focus) {
    outline: none;
    box-shadow: none;
}

.moment-upload-section {
    padding: 10px 15px 15px;
    border-top: 1px dashed #ebeef5;
}

:deep(.el-upload--picture-card) {
    --el-upload-picture-card-size: 100px;
}

:deep(.el-upload-list--picture-card .el-upload-list__item) {
    width: 100px;
    height: 100px;
}

@media screen and (max-width: 768px) {
    .create-post-container {
        padding: 10px;
    }

    .form-card {
        padding: 10px 5px;
    }

    .form-actions {
        flex-direction: column-reverse;
        /* Put primary button on top */
        gap: 10px;
        align-items: stretch;
    }

    .form-actions .el-button {
        width: 100%;
        margin-left: 0 !important;
    }
}
</style>
